const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const API_PREFIX = '/api/v1';

// Helper to get the access token
const getAccessToken = async (): Promise<string> => {
  try {
    const readTokenFromStorage = (storage: Storage, key: string) => {
      try {
        const session = storage.getItem(key);
        if (!session) return null;
        const data = JSON.parse(session);
        return data?.token || null;
      } catch {
        return null;
      }
    };
    
    let token = readTokenFromStorage(sessionStorage, 'descope.session');
    if (!token) {
      token = readTokenFromStorage(localStorage, 'descope.session');
    }
    if (!token && (window as any).__DESCOPE_JWT__) {
      token = (window as any).__DESCOPE_JWT__;
    }
    
    return token || '';
  } catch (error) {
    console.error('Error getting access token:', error);
    return '';
  }
};

// Helper for direct API fetch
const directApiFetch = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAccessToken();
    
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${API_PREFIX}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

export interface SuperAdminStats {
  totalUsers: number;
  totalOrganizations: number;
  totalTaskTemplates: number;
  totalSubmissions: number;
  totalActiveSubscriptions: number;
  totalRevenue: number;
  platformHealth: {
    database: string;
    stripe: string;
    lastBackup: string;
    systemLoad: string;
  };
}

export interface UserUpdate {
  email?: string;
  name?: string;
  role?: string;
  organization_id?: string;
  status?: string;
  is_admin?: boolean;
}

export interface OrganizationUpdate {
  name?: string;
  subscription_plan_id?: string;
  subscription_status?: string;
  task_limit?: number;
  can_use_byo_github?: boolean;
}

export interface BulkOperationRequest {
  ids: string[];
  action: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    total: number;
    pages: number;
    current: number;
    limit: number;
  };
}

export const superAdminApi = {
  // Dashboard Stats
  getDashboardStats: async (): Promise<SuperAdminStats> => {
    return directApiFetch('/superadmin/stats');
  },

  // User Management
  listUsers: async (params?: {
    search?: string;
    role?: string;
    organization_id?: string;
    page?: number;
    limit?: number;
  }): Promise<{ users: any[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.organization_id) queryParams.append('organization_id', params.organization_id);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    return directApiFetch(`/superadmin/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
  },

  updateUser: async (userId: string, data: UserUpdate): Promise<any> => {
    return directApiFetch(`/superadmin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteUser: async (userId: string): Promise<any> => {
    return directApiFetch(`/superadmin/users/${userId}`, {
      method: 'DELETE',
    });
  },

  bulkDeleteUsers: async (userIds: string[]): Promise<any> => {
    return directApiFetch('/superadmin/bulk/users/delete', {
      method: 'POST',
      body: JSON.stringify({ ids: userIds, action: 'delete' }),
    });
  },

  // Organization Management
  listOrganizations: async (params?: {
    search?: string;
    subscription_status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ organizations: any[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.subscription_status) queryParams.append('subscription_status', params.subscription_status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    return directApiFetch(`/superadmin/organizations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
  },

  updateOrganization: async (orgId: string, data: OrganizationUpdate): Promise<any> => {
    return directApiFetch(`/superadmin/organizations/${orgId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteOrganization: async (orgId: string): Promise<any> => {
    return directApiFetch(`/superadmin/organizations/${orgId}`, {
      method: 'DELETE',
    });
  },

  bulkDeleteOrganizations: async (orgIds: string[]): Promise<any> => {
    return directApiFetch('/superadmin/bulk/organizations/delete', {
      method: 'POST',
      body: JSON.stringify({ ids: orgIds, action: 'delete' }),
    });
  },

  // Subscription Management
  listSubscriptions: async (params?: {
    status?: string;
    plan_id?: string;
    page?: number;
    limit?: number;
  }): Promise<{ subscriptions: any[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.plan_id) queryParams.append('plan_id', params.plan_id);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    return directApiFetch(`/superadmin/subscriptions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
  },

  cancelSubscription: async (orgId: string, immediately: boolean = false): Promise<any> => {
    return directApiFetch(`/superadmin/subscriptions/${orgId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ immediately }),
    });
  },

  changeSubscriptionPlan: async (orgId: string, newPlanId: string): Promise<any> => {
    return directApiFetch(`/superadmin/subscriptions/${orgId}/change-plan`, {
      method: 'POST',
      body: JSON.stringify({ new_plan_id: newPlanId }),
    });
  },

  // Task Management
  listTasks: async (params?: {
    search?: string;
    organization_id?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ tasks: any[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.organization_id) queryParams.append('organization_id', params.organization_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    return directApiFetch(`/superadmin/tasks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
  },

  deleteTask: async (taskId: string): Promise<any> => {
    return directApiFetch(`/superadmin/tasks/${taskId}`, {
      method: 'DELETE',
    });
  },

  // System Operations
  cleanupOrphanedData: async (): Promise<any> => {
    return directApiFetch('/superadmin/system/cleanup-orphans', {
      method: 'POST',
    });
  },

  resetUserPassword: async (userId: string): Promise<any> => {
    return directApiFetch(`/superadmin/system/reset-user-password/${userId}`, {
      method: 'POST',
    });
  },
}; 